/**
 * PublicNav — CMS-driven public header (Round 40 · Task 7).
 *
 * Pulls its logo/nav labels from `site_content/header_nav` (heading = brand
 * title; body = pipe-separated "Label|href" lines, one per menu item) and the
 * quick contact strip from `site_content/header_contact` (heading = email,
 * subheading = phone, body = website).
 *
 * Falls back silently when the CMS block hasn't loaded yet so first paint is
 * never broken.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { List, X, EnvelopeSimple, Phone } from "@phosphor-icons/react";
import { Button } from "./ui/button";

const SBL_LOGO = "https://customer-assets.emergentagent.com/job_wisdom-hub-52/artifacts/cm4dyito_SBL%20Logo.jpeg";

function parseLinks(body) {
  return (body || "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
    .map((line) => {
      const [label, href] = line.split("|").map((s) => (s || "").trim());
      return { label: label || line, href: href || "#" };
    });
}

export default function PublicNav() {
  const [nav, setNav] = useState(null);
  const [contact, setContact] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    api.get("/site-content/header_nav").then((r) => setNav(r.data)).catch(() => {});
    api.get("/site-content/header_contact").then((r) => setContact(r.data)).catch(() => {});
  }, []);

  const items = parseLinks(nav?.body);
  const brand = nav?.heading || "SBL";

  return (
    <header className="bg-white border-b border-slate-200 sticky top-0 z-40" data-testid="public-nav">
      {contact?.heading && (
        <div className="bg-slate-950 text-slate-100 text-xs">
          <div className="max-w-6xl mx-auto px-4 sm:px-8 py-1.5 flex flex-wrap gap-4 items-center justify-end">
            <span className="inline-flex items-center gap-1"><EnvelopeSimple size={12} /> {contact.heading}</span>
            {contact?.subheading && <span className="inline-flex items-center gap-1"><Phone size={12} /> {contact.subheading}</span>}
          </div>
        </div>
      )}
      <div className="max-w-6xl mx-auto px-4 sm:px-8 h-16 flex items-center gap-4">
        <Link to="/" className="flex items-center gap-2" data-testid="public-nav-brand">
          <img src={SBL_LOGO} alt="SBL" className="w-9 h-9 object-contain rounded-sm" />
          <span className="text-base font-bold tracking-tight text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {brand}
          </span>
        </Link>
        <nav className="hidden md:flex items-center gap-1 lg:gap-2 text-xs lg:text-sm font-medium text-slate-600 flex-1">
          {items.map((it) => (
            <Link
              key={it.label}
              to={it.href}
              className="px-3 py-2 hover:text-slate-950 transition-colors"
              data-testid={`public-nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {it.label}
            </Link>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-2">
          <Link to="/login" className="hidden sm:inline-block">
            <Button variant="ghost" className="rounded-none text-sm">Sign in</Button>
          </Link>
          <Link to="/register" className="hidden sm:inline-block">
            <Button className="rounded-none text-sm bg-blue-800 text-white hover:bg-blue-900">Apply</Button>
          </Link>
          <button className="md:hidden p-2" onClick={() => setMobileOpen((v) => !v)} data-testid="public-nav-mobile-toggle">
            {mobileOpen ? <X size={18} /> : <List size={18} />}
          </button>
        </div>
      </div>
      {mobileOpen && (
        <div className="md:hidden border-t border-slate-200 bg-white">
          <nav className="p-4 space-y-1">
            {items.map((it) => (
              <Link
                key={it.label}
                to={it.href}
                className="block px-3 py-2 hover:bg-slate-50 text-sm text-slate-700"
                onClick={() => setMobileOpen(false)}
              >
                {it.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}

export { parseLinks };
